import { $, hideOverlay, showOverlay } from "@jpj/game-shell";
import type { Stage } from "../three/scene";
import { ISLANDS } from "./levels";
import { LOOT } from "./items";
import { deckValue } from "./board";
import { starsFor } from "./scoring";
import { state } from "./state";
import { openReveal } from "../reveal/reveal";

/**
 * Island lifecycle: load / unload / set sail / next, plus the HUD and the
 * quay cards. Pointer mechanics live in interaction.ts; the 3D drawing in
 * src/three; the rules in board.ts.
 */

let stage: Stage;
let onQuayTap: (shelfIdx: number) => void = () => {};

export function initFlow(s: Stage, quayTap: (shelfIdx: number) => void): void {
  stage = s;
  onQuayTap = quayTap;
}

export const island = () => ISLANDS[state.islandIdx];

export function loadIsland(k: number): void {
  const isl = ISLANDS[k];
  document.body.classList.remove("reveal-on");
  state.islandIdx = k;
  state.phase = "play";
  state.deck = { cols: isl.cols, rows: isl.rows, maxH: isl.maxH, placed: [] };
  state.shelf = isl.loot.map(() => null);
  state.selected = null;
  state.rotated = false;
  stage.setIsland(isl.cols, isl.rows, isl.maxH);
  stage.setRevealMode(false);
  $("islandBanner").innerHTML =
    `<b>${k + 1} · ${isl.name}</b><span class="ib-flavor"> — ${isl.flavor}</span>`;
  renderQuay();
  updateHud();
  setHint("Tap loot on the quay, then tap the deck. Flat tops carry more.");
}

export function unloadIsland(): void {
  state.deck.placed = [];
  state.shelf = state.shelf.map(() => null);
  state.selected = null;
  state.rotated = false;
  stage.clearLoot();
  stage.setGhost(null);
  renderQuay();
  updateHud();
}

/* ---------------- HUD + quay ---------------- */

export function updateHud(): void {
  const isl = island();
  $("islandVal").textContent = `${state.islandIdx + 1} / ${ISLANDS.length}`;
  $("holdVal").textContent = String(deckValue(state.deck));
  const quayLeft = isl.loot.reduce((s, key, i) => s + (state.shelf[i] === null ? LOOT[key].value : 0), 0);
  $("quayVal").textContent = String(quayLeft);
}

export function setHint(text: string): void {
  $("quayLabel").textContent = text;
}

export function renderQuay(): void {
  const isl = island();
  const loot = isl.loot;
  const host = $("quay");
  host.innerHTML = "";
  // One card per loot TYPE, with a counter of how many copies still wait on the
  // quay. data-shelf points at the first still-unpacked copy of that type (-1
  // when all are aboard) so the per-piece selection/placement logic is unchanged.
  const seen = new Set<string>();
  loot.forEach((key) => {
    if (seen.has(key)) return;
    seen.add(key);
    const t = LOOT[key];
    let remaining = 0, firstUnpacked = -1;
    loot.forEach((k, i) => {
      if (k !== key || state.shelf[i] !== null) return;
      remaining++;
      if (firstUnpacked === -1) firstUnpacked = i;
    });
    const selectedHere = state.selected !== null && loot[state.selected] === key;
    const card = document.createElement("button");
    card.type = "button";
    card.className = "loot-card" +
      (remaining === 0 ? " depleted" : "") +
      (selectedHere ? " selected" : "");
    card.dataset.key = key;
    card.dataset.shelf = String(firstUnpacked);
    card.title = `${t.name} — ${t.value} gold · ${t.w}×${t.d}×${t.h}` +
      (t.stackable ? " · takes load on top" : " · nothing stacks on it");
    card.innerHTML =
      `<span class="lc-emoji">${t.emoji}</span>` +
      `<span class="lc-name">${t.name}</span>` +
      `<span class="lc-meta"><span class="lc-dims">${t.w}×${t.d}×${t.h}</span>` +
      `<span class="lc-top ${t.stackable ? "ok" : "no"}">${t.stackable ? "▦" : "⛔"}</span></span>` +
      `<span class="lc-val">${t.value}</span>` +
      `<span class="lc-count">${remaining === 0 ? "⚓ aboard" : "×" + remaining}</span>`;
    if (firstUnpacked !== -1) card.addEventListener("click", () => onQuayTap(firstUnpacked));
    host.appendChild(card);
  });
  ($("rotateBtn") as HTMLButtonElement).disabled = state.selected === null ||
    LOOT[isl.loot[state.selected]].w === LOOT[isl.loot[state.selected]].d;
}

/* ---------------- set sail / between islands ---------------- */

export function submitIsland(): void {
  if (state.phase !== "play") return;
  const isl = island();
  const value = deckValue(state.deck);
  const stars = starsFor(value, isl);
  state.records[state.islandIdx] = {
    value,
    stars,
    placements: state.deck.placed.map((p) => ({ ...p })),
  };
  state.phase = "between";
  state.selected = null;
  stage.setGhost(null);
  $("ghostMsg").classList.add("hidden");

  $("ioTitle").textContent = `${isl.name} — plundered`;
  document.querySelectorAll<HTMLElement>("#ioStars .star").forEach((el, i) =>
    el.classList.toggle("on", i < stars));
  const left = isl.loot.reduce((s, key) => s + LOOT[key].value, 0) - value;
  $("ioLine").innerHTML =
    `<b class="gold">${value} gold</b> in the hold · ${left} left on the quay`;
  $("ioNote").textContent =
    stars === 3
      ? "Perfectly loaded — no deck in the fleet could carry more. The optimizer will tip its hat."
      : stars === 2
        ? "A solid haul — but the hold has secrets left. The optimizer is waiting at the last island."
        : "The quay still glitters… a quartermaster's work is never obvious. Reload if you dare.";
  const last = state.islandIdx === ISLANDS.length - 1;
  $("ioNext").textContent = last ? "Face the optimizer ▸" : "Next island ▸";
  showOverlay("islandOverlay");
}

export function nextFromOverlay(): void {
  hideOverlay("islandOverlay");
  if (state.islandIdx < ISLANDS.length - 1) {
    loadIsland(state.islandIdx + 1);
  } else {
    openReveal();
  }
}

export function retryIsland(): void {
  hideOverlay("islandOverlay");
  loadIsland(state.islandIdx);
}

export function restartRun(): void {
  for (let i = 0; i < state.records.length; i++) state.records[i] = null;
  hideOverlay("endOverlay");
  hideOverlay("solverOverlay");
  hideOverlay("islandOverlay");
  loadIsland(0);
}
